import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail, formatDateIT } from "@/lib/api";
import { emitServicesChanged, onServicesChanged } from "@/lib/servicesSync";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, ShieldCheck, Trash, PencilSimple, ArrowClockwise, Calendar, CurrencyEur, Clock, Bell } from "@phosphor-icons/react";

const CATEGORIES = [
  { v: "antivirus", l: "Antivirus" },
  { v: "dominio", l: "Dominio" },
  { v: "hosting", l: "Hosting" },
  { v: "email", l: "Email/Pec" },
  { v: "backup", l: "Backup" },
  { v: "abbonamento", l: "Abbonamento software" },
  { v: "altro", l: "Altro" },
];

const eur = (n) => (n ?? 0).toLocaleString("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 2 });

function todayPlusYear() {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
}

function todayPlus(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function daysBetween(dateStr) {
  if (!dateStr) return null;
  const target = new Date(dateStr + "T00:00:00");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.floor((target - today) / (1000 * 60 * 60 * 24));
}

export default function CustomerServicesSection({ customerId, customerEmail, customerSurname }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, editing: null });
  const [renewDialog, setRenewDialog] = useState({ open: false, service: null });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/customers/${customerId}/services`);
      // Escludi pacchetti minuti/assistenza — hanno UI dedicate (RemoteMinutesSection, ManagedPlanSection)
      // e non sono servizi ricorrenti a scadenza
      setItems(data.filter((s) => s.category !== "pacchetto_minuti" && s.category !== "pacchetto_assistenza"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  // Real-time sync: reload when services change from another page/tab
  useEffect(() => {
    const off = onServicesChanged(customerId, () => { load(); });
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { off(); document.removeEventListener("visibilitychange", onVis); };
  }, [customerId, load]);

  const removeService = async (s) => {
    if (!window.confirm(`Eliminare il servizio "${s.name}"? Lo storico verrà perso.`)) return;
    try {
      await api.delete(`/services/${s.id}`);
      toast.success("Servizio eliminato");
      emitServicesChanged(customerId);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const notifyEmptyPackage = (s) => {
    if (!customerEmail) {
      toast.error("Il cliente non ha un'email registrata");
      return;
    }
    const greeting = customerSurname ? `Salve Sig. ${customerSurname},` : "Salve,";
    const subject = `Pacchetto minuti assistenza remota — ${s.name}`;
    const body =
`${greeting}

Le comunichiamo che il pacchetto di minuti dedicato all'assistenza remota (${s.name}) risulta esaurito.

Per procedere con la fatturazione di un nuovo pacchetto, potrebbe indicarci di quanti minuti desidera ricaricare?

Se preferisce, possiamo inviarLe un report dettagliato dell'utilizzo effettuato fino ad oggi.

Restiamo a disposizione per qualsiasi chiarimento.`;
    window.location.href = `mailto:${encodeURIComponent(customerEmail)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const totalYear = items.reduce((s, it) => s + (it.price || 0), 0);

  return (
    <Card className="p-6 border-border/70 rounded-lg" data-testid="customer-services-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ShieldCheck size={20} weight="duotone" className="text-primary" />
          <div>
            <h2 className="font-display font-bold tracking-tight text-lg">Scadenzario cliente</h2>
            <div className="text-xs text-muted-foreground">
              {items.length} {items.length === 1 ? "servizio attivo" : "servizi attivi"} · Totale annuo <span className="font-mono font-semibold text-foreground">{eur(totalYear)}</span>
            </div>
          </div>
        </div>
        <Button
          onClick={() => setDialog({ open: true, editing: null })}
          data-testid="add-service-btn"
          className="rounded-full gap-1"
        >
          <Plus size={16} weight="bold" /> Nuovo servizio
        </Button>
      </div>

      {loading && <div className="text-sm text-muted-foreground">Caricamento…</div>}
      {!loading && items.length === 0 && (
        <div className="text-sm text-muted-foreground/70 font-mono border border-dashed border-border rounded-md p-6 text-center">
          — Nessun servizio attivo per questo cliente —
        </div>
      )}

      <div className="space-y-2">
        {items.map((s) => {
          const days = daysBetween(s.expires_at);
          const expired = days !== null && days < 0;
          const expiring = days !== null && days >= 0 && days <= 4;
          const toneCls = expired
            ? "border-rose-500/50 bg-rose-500/5"
            : expiring
            ? "border-amber-500/50 bg-amber-500/5"
            : "border-border/60";
          return (
            <div
              key={s.id}
              className={`flex flex-col sm:flex-row sm:items-center gap-3 justify-between border rounded-md px-3 py-2.5 ${toneCls}`}
              data-testid={`service-row-${s.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-medium">{s.name}</div>
                  {s.category && (
                    <Badge className="bg-secondary text-foreground border-border hover:bg-secondary font-mono text-[9px] uppercase">{s.category}</Badge>
                  )}
                  {expired && (
                    <Badge className="bg-rose-500/15 text-rose-700 dark:text-rose-400 border border-rose-500/40 hover:bg-rose-500/15 font-mono text-[9px] uppercase">
                      Scaduto {Math.abs(days)}gg fa
                    </Badge>
                  )}
                  {expiring && (
                    <Badge className="bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/40 hover:bg-amber-500/15 font-mono text-[9px] uppercase">
                      Scade in {days}gg
                    </Badge>
                  )}
                </div>
                <div className="text-xs text-muted-foreground font-mono mt-0.5 flex items-center gap-3 flex-wrap">
                  {s.category !== "pacchetto_minuti" && (
                    <span className="inline-flex items-center gap-1"><Calendar size={11} /> Scadenza: {formatDateIT(s.expires_at)}</span>
                  )}
                  {s.price != null && <span className="inline-flex items-center gap-1"><CurrencyEur size={11} /> {eur(s.price)}{s.category !== "pacchetto_minuti" ? "/anno" : ""}</span>}
                  {s.category === "pacchetto_minuti" && (
                    <span className={`inline-flex items-center gap-1 ${s.minutes_remaining === 0 ? "text-rose-600 dark:text-rose-400 font-semibold" : ""}`}>
                      <Clock size={11} /> {s.minutes_remaining ?? 0} / {s.minutes_total ?? 0} min residui
                    </span>
                  )}
                  {s.history?.length > 0 && (
                    <span className="inline-flex items-center gap-1"><Clock size={11} /> {s.history.length} rinnov{s.history.length === 1 ? "o" : "i"}</span>
                  )}
                </div>
                {s.notes && <div className="text-xs text-muted-foreground mt-1">{s.notes}</div>}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {s.category === "pacchetto_minuti" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => notifyEmptyPackage(s)}
                    className="rounded-full gap-1 border-amber-500/50 text-amber-700 dark:text-amber-400 hover:bg-amber-500/10"
                    data-testid={`notify-empty-service-${s.id}`}
                    title="Avvisa il cliente via email che il pacchetto minuti è esaurito"
                  >
                    <Bell size={14} /> Avvisa
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="default"
                  onClick={() => setRenewDialog({ open: true, service: s })}
                  className="rounded-full gap-1"
                  data-testid={`renew-service-${s.id}`}
                >
                  <ArrowClockwise size={14} /> Rinnova
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setDialog({ open: true, editing: s })}
                  data-testid={`edit-service-${s.id}`}
                  title="Modifica"
                >
                  <PencilSimple size={14} />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => removeService(s)}
                  className="text-destructive hover:text-destructive"
                  data-testid={`delete-service-${s.id}`}
                  title="Elimina"
                >
                  <Trash size={14} />
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <ServiceDialog
        open={dialog.open}
        onOpenChange={(o) => setDialog({ ...dialog, open: o })}
        editing={dialog.editing}
        customerId={customerId}
        onSaved={() => { setDialog({ open: false, editing: null }); emitServicesChanged(customerId); load(); }}
      />

      <RenewDialog
        open={renewDialog.open}
        onOpenChange={(o) => setRenewDialog({ ...renewDialog, open: o })}
        service={renewDialog.service}
        onDone={() => { setRenewDialog({ open: false, service: null }); emitServicesChanged(customerId); load(); }}
      />
    </Card>
  );
}

function ServiceDialog({ open, onOpenChange, editing, customerId, onSaved }) {
  const [form, setForm] = useState({ name: "", category: "altro", price: "", expires_at: "", notes: "", minutes_total: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        name: editing.name || "",
        category: editing.category || "altro",
        price: editing.price ?? "",
        expires_at: editing.expires_at || "",
        notes: editing.notes || "",
        minutes_total: editing.minutes_total ?? "",
      });
    } else {
      setForm({ name: "", category: "altro", price: "", expires_at: todayPlusYear(), notes: "", minutes_total: "" });
    }
  }, [open, editing]);

  const isMinutePackage = form.category === "pacchetto_minuti";

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nome richiesto");
    if (!isMinutePackage && !form.expires_at) return toast.error("Data scadenza richiesta");
    if (isMinutePackage) {
      const mt = parseInt(form.minutes_total, 10);
      if (!mt || mt <= 0) return toast.error("Per un pacchetto minuti indica un monte in minuti > 0");
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        category: form.category || null,
        price: form.price === "" ? null : parseFloat(form.price),
        expires_at: isMinutePackage ? null : form.expires_at,
        notes: form.notes.trim() || null,
        minutes_total: isMinutePackage ? parseInt(form.minutes_total, 10) : null,
      };
      if (editing) {
        await api.patch(`/services/${editing.id}`, payload);
        toast.success("Servizio aggiornato");
      } else {
        await api.post(`/customers/${customerId}/services`, payload);
        toast.success("Servizio creato");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="service-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Modifica servizio" : "Nuovo servizio"}</DialogTitle>
          <DialogDescription>Servizio ricorrente con scadenza per questo cliente.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-widest">Nome servizio</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Es. Antivirus Kaspersky, Pacchetto minuti Mario" data-testid="service-name-input" className="mt-1.5" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-widest">Categoria</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1.5" data-testid="service-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-widest">Prezzo annuo (€)</Label>
              <Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1.5 font-mono" data-testid="service-price-input" />
            </div>
          </div>
          {isMinutePackage && (
            <div className="border-l-2 border-primary/40 pl-3 py-2 bg-primary/5 rounded-r">
              <Label className="font-mono text-[10px] uppercase tracking-widest">Monte minuti totale</Label>
              <Input
                type="number"
                min="0"
                step="1"
                placeholder="Es. 600"
                value={form.minutes_total}
                onChange={(e) => setForm({ ...form, minutes_total: e.target.value })}
                data-testid="service-minutes-input"
                className="mt-1.5 font-mono"
              />
              <div className="text-[10px] text-muted-foreground mt-1 font-mono">
                Espresso in minuti. Ogni assistenza scalerà da qui.
                {form.minutes_total && ` = ${Math.floor(parseInt(form.minutes_total || 0, 10) / 60)}h ${parseInt(form.minutes_total || 0, 10) % 60}min`}
              </div>
            </div>
          )}
          {!isMinutePackage && (
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-widest">Scadenza</Label>
            <Input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="mt-1.5" data-testid="service-expires-input" />
          </div>
          )}
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-widest">Note</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" data-testid="service-notes-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="service-save-btn" className="rounded-full gap-1">
            <Plus size={14} /> {saving ? "Salvo…" : (editing ? "Aggiorna" : "Crea servizio")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RenewDialog({ open, onOpenChange, service, onDone }) {
  const [form, setForm] = useState({ new_expires_at: "", price: "", notes: "", add_minutes: "" });
  const [saving, setSaving] = useState(false);
  const isMinutePackage = service?.category === "pacchetto_minuti";

  useEffect(() => {
    if (!open || !service) return;
    let base = service.expires_at ? new Date(service.expires_at + "T00:00:00") : new Date();
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (base < today) base = today;
    base.setFullYear(base.getFullYear() + 1);
    setForm({
      new_expires_at: base.toISOString().slice(0, 10),
      price: service.price ?? "",
      notes: "",
      add_minutes: "",
    });
  }, [open, service]);

  const submit = async () => {
    if (!isMinutePackage && !form.new_expires_at) return toast.error("Nuova scadenza richiesta");
    if (isMinutePackage) {
      const m = parseInt(form.add_minutes, 10);
      if (!m || m <= 0) return toast.error("Indica i minuti acquistati nel rinnovo (> 0)");
    }
    setSaving(true);
    try {
      await api.post(`/services/${service.id}/renew`, {
        new_expires_at: isMinutePackage ? null : form.new_expires_at,
        price: form.price === "" ? null : parseFloat(form.price),
        notes: form.notes || null,
        add_minutes: isMinutePackage ? parseInt(form.add_minutes, 10) : null,
      });
      toast.success(isMinutePackage ? "Nuovo pacchetto minuti attivato" : "Servizio rinnovato");
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const purchased = parseInt(form.add_minutes || 0, 10) || 0;
  const residual = service?.minutes_remaining ?? 0;
  const newTotal = purchased + residual;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="renew-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowClockwise size={20} weight="duotone" className="text-primary" />
            {isMinutePackage ? "Ricarica pacchetto minuti" : "Rinnova servizio"}
          </DialogTitle>
          <DialogDescription>
            {service?.name}
            {!isMinutePackage && (
              <> · scadenza attuale <span className="font-mono">{formatDateIT(service?.expires_at)}</span></>
            )}
            {isMinutePackage && (
              <span className="block mt-1">
                Monte attuale: <span className="font-mono">{service?.minutes_total || 0} min</span> · Residui:{" "}
                <span className="font-mono">{service?.minutes_remaining ?? 0} min</span>
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {!isMinutePackage && (
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-widest">Nuova scadenza</Label>
            <Input type="date" value={form.new_expires_at} onChange={(e) => setForm({ ...form, new_expires_at: e.target.value })} className="mt-1.5" data-testid="renew-date-input" />
            <div className="flex gap-1 mt-2">
              <Button size="sm" variant="outline" onClick={() => setForm({ ...form, new_expires_at: todayPlus(180) })} className="rounded-full text-xs">+6 mesi</Button>
              <Button size="sm" variant="outline" onClick={() => setForm({ ...form, new_expires_at: todayPlus(365) })} className="rounded-full text-xs">+1 anno</Button>
              <Button size="sm" variant="outline" onClick={() => setForm({ ...form, new_expires_at: todayPlus(730) })} className="rounded-full text-xs">+2 anni</Button>
            </div>
          </div>
          )}
          {isMinutePackage && (
            <div className="border-l-2 border-primary/40 pl-3 py-2 bg-primary/5 rounded-r">
              <Label className="font-mono text-[10px] uppercase tracking-widest">Minuti acquistati nel rinnovo</Label>
              <Input
                type="number"
                min="1"
                step="1"
                placeholder="Es. 300"
                value={form.add_minutes}
                onChange={(e) => setForm({ ...form, add_minutes: e.target.value })}
                data-testid="renew-add-minutes-input"
                className="mt-1.5 font-mono"
              />
              <div className="text-[11px] text-muted-foreground mt-2 font-mono leading-relaxed">
                Nuovo monte = <span className="font-semibold text-foreground">{purchased} min acquistati</span> + <span className="font-semibold text-foreground">{residual} min residui</span>
                {purchased > 0 && <> = <span className="font-bold text-primary">{newTotal} min disponibili</span></>}
                <div className="mt-1">Il conteggio d'uso si azzera; i minuti già consumati restano nello storico ma non tornano nel monte.</div>
              </div>
            </div>
          )}
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-widest">Prezzo del rinnovo (€)</Label>
            <Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1.5 font-mono" data-testid="renew-price-input" />
            <div className="text-[10px] text-muted-foreground mt-1 font-mono">Se lasci vuoto, mantiene il prezzo attuale.</div>
          </div>
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-widest">Note (opzionale)</Label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" data-testid="renew-notes-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={submit} disabled={saving} data-testid="renew-submit-btn" className="rounded-full gap-1">
            <ArrowClockwise size={14} /> {saving ? "Salvo…" : (isMinutePackage ? "Conferma ricarica" : "Conferma rinnovo")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
